<i18n
  src="@/i18n/components/client-settings/coordination/conversion-attribute/coordination-conversion-attribute-edit-table.json"
></i18n>
<template>
  <div class="conversionAttributeEditTable">
    <SettingsTable>
      <SettingsTableRow>
        <SettingsTableCell
          v-t="'targetData'"
          width="200px"
          :style="{ padding: '20px 10px 20px 20px' }"
        />
        <SettingsTableCell width="480px">
          {{ coordinationConversionAttribute.targetData }}
        </SettingsTableCell>
      </SettingsTableRow>
      <SettingsTableRow>
        <SettingsTableCell
          width="200px"
          :style="{ padding: '20px 10px 20px 20px' }"
        >
          {{ $t("conversionAttribute") }}
          <span class="conversionAttributeEditTableCell__required">{{
            $t("required")
          }}</span>
        </SettingsTableCell>
        <SettingsTableCell width="480px">
          <template v-if="!conversionAttributeLimitReached">
            <RadioButton
              :value="coordinationConversionAttribute.creationMethod"
              :label="CreationMethod.NEW"
              @change="onSelectCreationMethod"
              >{{ $t("newConversionAttribute") }}
            </RadioButton>
            <div
              class="conversionAttributeEditTable__conversionAttributeMethodContent"
            >
              <InputText
                :value="coordinationConversionAttribute.conversionAttributeName"
                :placeholder="$t('conversionAttributePlaceholder')"
                :max-length="40"
                @input="onInputConversionAttributeName"
              />
              <span
                class="conversionAttributeEditTableCell__conversionAttributeType"
              >
                {{ $t("attributeType") }}
                <SelectBox
                  class="conversionAttributeEditTableCell__conversionAttributeTypeSelect"
                  :value="
                    coordinationConversionAttribute.conversionAttributeType
                  "
                  :options="selectConversionAttributeTypeOptions"
                  @input="onSelectConversionAttributeType"
                />
              </span>
            </div>
          </template>
          <RadioButton
            :value="coordinationConversionAttribute.creationMethod"
            :label="CreationMethod.EXISTING"
            @change="onSelectCreationMethod"
            >{{ $t("existingConversionAttribute") }}
          </RadioButton>
          <div
            class="conversionAttributeEditTable__conversionAttributeMethodContent"
          >
            <SelectBox
              :value="coordinationConversionAttribute.conversionAttributeId"
              :options="selectConversionAttributeOptions"
              @input="onSelectConversionAttribute"
            />
          </div>
          <div
            v-if="conversionAttributeLimitReached"
            class="conversionAttributeEditTable__conversionAttributeLimitText"
            v-html="conversionAttributeLimitText"
          />
        </SettingsTableCell>
      </SettingsTableRow>
      <SettingsTableRow>
        <SettingsTableCell
          class="conversionAttributeEditTable__inputParameterHeader"
          :style="{ padding: '20px 10px 20px 20px' }"
        >
          <span v-t="'coordinationParameter'" />
          <Tooltip :placement="tooltipPlacement.Bottom">
            <Icon
              :icon="icons.Help"
              :color="color.Base600"
              style="margin-left: 4px"
            />
            <div
              slot="content"
              v-t="'conversionAttributeParameterTooltipText'"
              class="conversionAttributeEditTable__tooltipContent"
            />
          </Tooltip>
          <span class="conversionAttributeEditTableCell__required">{{
            $t("required")
          }}</span>
        </SettingsTableCell>
        <SettingsTableCell>
          <div
            v-for="(param, index) in coordinationConversionAttribute.path"
            :key="index"
          >
            <div class="conversionAttributeEditTable__inputParameter">
              <InputText
                :value="param"
                class="conversionAttributeEditTable__inputParameterForm"
                :placeholder="$t('conversionAttributeParameterPlaceholder')"
                :max-length="40"
                :has-error="isValidCoordinationConversionAttributeData(param)"
                @input="onInputParam($event, index)"
              />
              <IconButton
                v-show="numForm > 1"
                :icon="icons.ButtonClose"
                :size="18"
                @click="onClickDelete(index)"
              />
            </div>
            <div
              v-show="isValidCoordinationConversionAttributeData(param)"
              v-t="'errorText'"
              class="conversionAttributeEditTable__errorText"
            />
          </div>

          <div
            v-if="canAddParameter"
            class="conversionAttributeEditTable__addParameter"
            @click="onClickNext"
          >
            <IconButton
              :size="18"
              :icon="icons.PlusCircle"
              class="conversionAttributeEditTable__addParameterIcon"
            />
            <span v-t="'addConversionAttributeParameter'" />
          </div>
          <div
            v-t="'parameterDescription'"
            class="conversionAttributeEditTable__parameterDescription"
          />
        </SettingsTableCell>
      </SettingsTableRow>
    </SettingsTable>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Emit } from "vue-property-decorator";
import SettingsTable from "@/components/table/SettingsTable.vue";
import SettingsTableRow from "@/components/table/SettingsTableRow.vue";
import SettingsTableCell from "@/components/table/SettingsTableCell.vue";
import Icon from "@/components/Icon.vue";
import IconButton from "@/components/IconButton.vue";
import RadioButton from "@/components/form/RadioButton.vue";
import SelectOption from "@/components/form/SelectOption";
import SelectBox from "@/components/form/SelectBox.vue";
import InputText from "@/components/form/InputText.vue";
import Tooltip from "@/components/Tooltip.vue";
import { Colors } from "@/const/Colors";
import { Icons } from "@/const/Icons";
import { TooltipPlacement } from "@/const/tooltip";
import {
  ConversionAttributeType,
  ConversionAttributeDefinition,
  CoordinationConversionAttributeEditableData,
  isInValidConversionAttributeParam
} from "@/models/client-settings/ConversionAttributeDefinition";
import { CreationMethod } from "@/models/client-settings/Coordination";

const EMPTY_CONVERSION_ATTRIBUTE_VALUE = 0;

@Component({
  components: {
    Icon,
    IconButton,
    RadioButton,
    SelectBox,
    InputText,
    SettingsTable,
    SettingsTableRow,
    SettingsTableCell,
    Tooltip
  }
})
export default class coordinationConversionAttributeEditTable extends Vue {
  CreationMethod = CreationMethod;
  color = Colors;
  icons = Icons;
  tooltipPlacement = TooltipPlacement;

  @Prop({ type: Object, required: true })
  coordinationConversionAttribute!: CoordinationConversionAttributeEditableData;

  @Prop({ type: Boolean, default: false })
  conversionAttributeLimitReached!: boolean;

  @Prop({ type: Boolean, required: true })
  isContractWeb!: boolean;

  @Emit("update")
  updateCoordinationConversionAttribute(
    data: CoordinationConversionAttributeEditableData
  ) {
    return data;
  }

  get conversionAttributeLimitText() {
    return this.isContractWeb
      ? this.$t("conversionAttributeLimit")
      : this.$t("conversionAttributeLimitAppOnly");
  }

  get activeConversionAtributes(): ConversionAttributeDefinition[] {
    return this.$store.state.clientSettings
      .activeConversionAttributeDefinitions;
  }

  get selectConversionAttributeOptions(): SelectOption[] {
    return [
      {
        label: "-----------",
        value: EMPTY_CONVERSION_ATTRIBUTE_VALUE,
        disabled: false
      },
      ...this.activeConversionAtributes.map(conversionAttribute => ({
        label: conversionAttribute.name,
        value: conversionAttribute.id,
        disabled: false
      }))
    ];
  }

  get selectConversionAttributeTypeOptions(): SelectOption[] {
    return [
      {
        label: this.$t("text") as string,
        value: "TEXT",
        disabled: false
      },
      {
        label: this.$t("number") as string,
        value: "NUMBER",
        disabled: false
      }
    ];
  }

  get numForm(): number {
    return this.coordinationConversionAttribute.path.length;
  }

  get canAddParameter() {
    return this.numForm < 5;
  }

  onInputConversionAttributeName(value: string) {
    this.updateCoordinationConversionAttribute({
      ...this.coordinationConversionAttribute,
      conversionAttributeName: value.trim()
    });
  }

  onSelectCreationMethod(value: CreationMethod) {
    this.updateCoordinationConversionAttribute({
      ...this.coordinationConversionAttribute,
      creationMethod: value
    });
  }

  onSelectConversionAttributeType(value: ConversionAttributeType) {
    this.updateCoordinationConversionAttribute({
      ...this.coordinationConversionAttribute,
      conversionAttributeType: value
    });
  }

  onSelectConversionAttribute(value: string) {
    this.updateCoordinationConversionAttribute({
      ...this.coordinationConversionAttribute,
      conversionAttributeId: Number(value)
    });
  }

  onInputParam(value: string, index: number) {
    const newParams = [];
    const currentParams = [...this.coordinationConversionAttribute.path];
    for (let i = 0; i < this.numForm; i++) {
      const newParam = i === index ? value.trim() : currentParams[i] || "";
      newParams.push(newParam);
    }

    this.updateCoordinationConversionAttribute({
      ...this.coordinationConversionAttribute,
      path: newParams
    });
  }

  onClickDelete(index: number) {
    const newParams = [...this.coordinationConversionAttribute.path];
    newParams.splice(index, 1);

    this.updateCoordinationConversionAttribute({
      ...this.coordinationConversionAttribute,
      path: newParams
    });
  }

  onClickNext() {
    this.updateCoordinationConversionAttribute({
      ...this.coordinationConversionAttribute,
      path: [...this.coordinationConversionAttribute.path, ""]
    });
  }

  isValidCoordinationConversionAttributeData(path: string): boolean {
    return isInValidConversionAttributeParam(path);
  }
}
</script>

<style lang="scss" scoped>
.conversionAttributeEditTable {
  margin-bottom: 16px;
  width: 680px;
}
.conversionAttributeEditTableCell__required {
  color: #c93636;
  display: inline;
  float: right;
  margin-left: 10px;
}
.conversionAttributeEditTable__conversionAttributeMethodContent {
  margin-left: 35px;
}

.conversionAttributeEditTableCell__conversionAttributeType {
  display: flex;
  align-items: center;
  margin: 10px 0px 10px 25px;
}
.conversionAttributeEditTableCell__conversionAttributeTypeSelect {
  margin-left: 10px;
}
.conversionAttributeEditTable__conversionAttributeLimitText {
  margin-top: 12px;

  ::v-deep a {
    color: $colorBase700;
    text-decoration: underline;
    &:hover {
      color: $colorBase800;
    }
  }
}
.conversionAttributeEditTable__tooltipContent {
  width: 226px;
  white-space: pre-line;
  word-wrap: break-word;
  line-height: 1.5;
}
.conversionAttributeEditTable__inputParameterHeader > * {
  display: inline-flex;
  vertical-align: middle;
}
.conversionAttributeEditTable__inputParameter {
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}
.conversionAttributeEditTable__inputParameterForm {
  width: 400px;
  margin-right: 8px;
}
.conversionAttributeEditTable__errorText {
  margin-bottom: 10px;
  color: $colorError;
}
.conversionAttributeEditTable__addParameter {
  display: inline-flex;
  font-size: 13px;
  align-items: center;
  cursor: pointer;
}
.conversionAttributeEditTable__addParameterIcon {
  margin-right: 5px;
}
.conversionAttributeEditTable__parameterDescription {
  margin-top: 6px;
  color: $colorBase700;
}
</style>
